// frontend/src/pages/doctor/DoctorDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Calendar, DollarSign, TrendingUp, 
  Clock, CheckCircle, AlertCircle
} from 'lucide-react';
import doctorService from '../../services/doctorService';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const DoctorDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, consultationsRes, subscriptionRes] = await Promise.all([
        doctorService.getStats(),
        doctorService.getConsultations({ status: 'pending', limit: 5 }),
        doctorService.getSubscription()
      ]);

      if (statsRes.success) {
        setStats(statsRes.data);
      }

      if (consultationsRes.success) {
        setConsultations(consultationsRes.data);
      }

      if (subscriptionRes.success) {
        setSubscriptionData(subscriptionRes.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  const usage = subscriptionData?.usage || {};
  const pendingPayment = subscriptionData?.pendingPayment;

  const statCards = [
    {
      title: 'Total Consultations',
      value: stats?.totalConsultations || 0,
      icon: Users,
      color: 'bg-blue-500',
      textColor: 'text-blue-600'
    },
    {
      title: 'Pending Requests',
      value: stats?.pendingConsultations || 0,
      icon: Clock,
      color: 'bg-yellow-500',
      textColor: 'text-yellow-600'
    },
    {
      title: 'Completed Today',
      value: stats?.todayConsultations || 0,
      icon: CheckCircle,
      color: 'bg-green-500',
      textColor: 'text-green-600'
    },
    {
      title: 'Monthly Earnings',
      value: `KES ${(stats?.monthlyEarnings || 0)?.toLocaleString()}`,
      icon: DollarSign,
      color: 'bg-purple-500',
      textColor: 'text-purple-600'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Doctor Dashboard</h1>
          <p className="text-gray-600 mt-1">Manage your consultations and patients</p>
        </div>
        <Link
          to="/doctor/consultations"
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          View All Consultations
        </Link>
      </div>

      {/* Pending Payment Alert */}
      {pendingPayment && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-lg">
          <div className="flex items-start">
            <Clock className="w-5 h-5 text-yellow-500 mt-0.5 mr-3" />
            <div>
              <h3 className="font-semibold text-yellow-800">Payment Pending Approval</h3>
              <p className="text-yellow-700 text-sm mt-1">
                Your {pendingPayment.plan?.toUpperCase()} plan payment (KES {pendingPayment.amount?.toLocaleString()}) 
                is awaiting admin approval. Submitted on {new Date(pendingPayment.submittedAt)?.toLocaleDateString()}.
              </p>
              <p className="text-yellow-600 text-xs mt-2">
                Your subscription will be activated once the payment is verified.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Consultation Limit Reached Alert */}
      {usage && !usage.canAccept && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3" />
            <div>
              <h3 className="font-semibold text-red-800">Consultation Limit Reached</h3>
              <p className="text-red-700 text-sm mt-1">
                You've reached your monthly consultation limit ({usage.limit} consultations).
                <Link to="/doctor/subscription" className="underline ml-1 font-medium">
                  Upgrade your plan
                </Link> to accept more consultations.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Usage Indicator */}
      {usage && usage.canAccept && (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900">Monthly Consultation Usage</h3>
              <p className="text-blue-700 text-sm mt-1">
                {usage.used} of {usage.remaining === 'unlimited' ? '∞' : usage.limit} consultations used
                {usage.plan !== 'free' && (
                  <span className="ml-2 px-2 py-1 bg-blue-200 text-blue-800 rounded text-xs font-medium">
                    {usage.plan?.toUpperCase()} PLAN
                  </span>
                )}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-600">
                {usage.remaining === 'unlimited' ? '∞' : usage.remaining}
              </p>
              <p className="text-xs text-blue-600">remaining</p>
            </div>
          </div>
          <div className="mt-3">
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{
                  width: usage.remaining === 'unlimited'
                    ? '100%'
                    : `${Math.min(100, (usage.used / usage.limit) * 100)}%`
                }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending Consultations */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Pending Consultation Requests</h2>
            <Link
              to="/doctor/consultations?status=pending"
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              View All
            </Link>
          </div>

          {consultations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No pending consultation requests</p>
            </div>
          ) : (
            <div className="space-y-4">
              {consultations.map((consultation) => (
                <div
                  key={consultation._id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-gray-900">
                          {consultation.patient?.name || 'Anonymous Patient'}
                        </h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          consultation.type === 'online'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {consultation.type}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        <strong>Symptoms:</strong> {consultation.symptoms}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Requested: {new Date(consultation.createdAt)?.toLocaleString()}
                      </p>
                      {consultation.preferredTime && (
                        <p className="text-xs text-gray-500">
                          Preferred time: {new Date(consultation.preferredTime)?.toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="ml-4">
                      <Link
                        to={`/doctor/consultations/${consultation._id}`}
                        className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm"
                      >
                        Review
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="space-y-6">
          {/* Rating Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Your Rating</h3>
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-500 mb-2">
                {stats?.rating?.average?.toFixed(1) || '0.0'}
              </div>
              <div className="flex justify-center items-center space-x-1 mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={`text-2xl ${
                      star <= (stats?.rating?.average || 0)
                        ? 'text-yellow-500'
                        : 'text-gray-300'
                    }`}
                  >
                    ★
                  </span>
                ))}
              </div>
              <p className="text-sm text-gray-600">
                Based on {stats?.rating?.count || 0} reviews
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link
                to="/doctor/schedule"
                className="block w-full px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-center"
              >
                <Calendar className="w-4 h-4 inline mr-2" />
                Manage Schedule
              </Link>
              <Link
                to="/doctor/patients"
                className="block w-full px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-center"
              >
                <Users className="w-4 h-4 inline mr-2" />
                View Patients
              </Link>
              <Link
                to="/doctor/earnings"
                className="block w-full px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-center"
              >
                <TrendingUp className="w-4 h-4 inline mr-2" />
                View Earnings
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorDashboard;